<template>
  <!-- HERO -->
  <TopBanner
    layout="full-width"
    currentPage="Login"
    heading="Login"
    bannerClass="banner-auth"
  />

  <!-- CONTENT -->
  <section class="py-16 md:py-24 bg-slate-50/40">
    <div class="max-w-7xl mx-auto px-6 lg:px-10 xl:px-14">
      <div class="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        
        <!-- LEFT SIDE: Marketing/Trust Column (Login page) -->
        <div class="lg:col-span-6 space-y-8 lg:pr-6 order-2 lg:order-1">
          <div>
            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/50 uppercase tracking-widest">
              Trusted Global Network
            </span>
            <h2 class="text-3xl md:text-4xl font-extrabold text-slate-900 mt-4 leading-tight">
              Your Premium Chauffeur Service Partner
            </h2>
            <p class="text-slate-600 mt-4 text-[16px] leading-relaxed">
              Welcome back to Vehitra World. Sign in to your portal to manage your chauffeur bookings, download Statements of Account, track vehicle routes, and book premium ground transportation worldwide.
            </p>
          </div>

          <!-- Features list -->
          <div class="space-y-6">
            <h3 class="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Why Travel With Us</h3>
            
            <div class="flex gap-4">
              <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                <i class="pi pi-shield text-lg"></i>
              </div>
              <div>
                <h4 class="text-[16px] font-bold text-slate-800">Safety &amp; Compliance First</h4>
                <p class="text-sm text-slate-500 mt-1 leading-relaxed">All rides are backed by fully-insured vehicles and professional, local chauffeurs trained in executive client service.</p>
              </div>
            </div>

            <div class="flex gap-4">
              <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                <i class="pi pi-map-marker text-lg"></i>
              </div>
              <div>
                <h4 class="text-[16px] font-bold text-slate-800">Global Reach, Consistent Quality</h4>
                <p class="text-sm text-slate-500 mt-1 leading-relaxed">Access clean, premium Mercedes-Benz, BMW, and executive SUVs with identical service quality in every major destination.</p>
              </div>
            </div>

            <div class="flex gap-4">
              <div class="shrink-0 h-12 w-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100 shadow-sm">
                <i class="pi pi-phone text-lg"></i>
              </div>
              <div>
                <h4 class="text-[16px] font-bold text-slate-800">24/7 Operations Support</h4>
                <p class="text-sm text-slate-500 mt-1 leading-relaxed">Our operations room monitors every flight and coordinates with chauffeurs globally to guarantee a flawless transfer.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- RIGHT SIDE: The Form Card -->
        <div class="lg:col-span-6 w-full order-1 lg:order-2">
          <div
            class="rounded-2xl bg-white/80 supports-[backdrop-filter]:backdrop-blur-md
                   shadow-xl ring-1 ring-black/5 p-6 md:p-8"
          >
            <h1 class="text-slate-800 text-2xl md:text-3xl font-semibold tracking-tight">
              Welcome back
            </h1>
            <p class="text-slate-500 mt-1 text-sm">
              Sign in to continue to your account.
            </p>

            <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
              <!-- Email -->
              <div>
                <label for="login-username" class="block text-[12px] uppercase tracking-widest text-slate-500">
                  Username or email address *
                </label>
                <div
                  class="mt-2 flex items-center rounded-xl border border-slate-200 bg-white/95
                         focus-within:ring-2 focus-within:ring-emerald-500/30 focus-within:border-emerald-400
                         transition"
                >
                  <span class="pl-3 pr-2 text-slate-400">
                    <!-- Mail icon -->
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="#64748b" stroke-width="2"/>
                      <path d="M22 6l-10 7L2 6" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                  <input
                    v-model="email"
                    id="login-username"
                    type="text"
                    autocomplete="username"
                    class="flex-1 h-12 md:h-[52px] bg-transparent outline-none text-slate-800 placeholder-slate-400
                           px-2 md:px-3 text-[15px]"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <!-- Password -->
              <div>
                <label for="login-password" class="block text-[12px] uppercase tracking-widest text-slate-500">
                  Password *
                </label>
                <div
                  class="mt-2 flex items-center rounded-xl border border-slate-200 bg-white/95
                         focus-within:ring-2 focus-within:ring-emerald-500/30 focus-within:border-emerald-400
                         transition"
                >
                  <span class="pl-3 pr-2 text-slate-400">
                    <!-- Lock icon -->
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="11" width="18" height="10" rx="2" stroke="#64748b" stroke-width="2"/>
                      <path d="M7 11V8a5 5 0 0110 0v3" stroke="#64748b" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </span>
                  <input
                    v-model="password"
                    :type="passwordFieldtype"
                    id="login-password"
                    autocomplete="current-password"
                    class="flex-1 h-12 md:h-[52px] bg-transparent outline-none text-slate-800 placeholder-slate-400
                           px-2 md:px-3 text-[15px]"
                    placeholder="••••••••"
                    @keydown.enter="handleLogin"
                  />
                  <button
                    type="button"
                    class="pr-3 pl-2 text-slate-500 hover:text-slate-700 transition"
                    @click="togglePasswordFieldType"
                    :aria-label="passwordFieldtype === 'password' ? 'Show password' : 'Hide password'"
                  >
                    <!-- Eye / Eye-off -->
                    <svg v-if="passwordFieldtype === 'password'" width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="#64748b" stroke-width="2"/>
                      <circle cx="12" cy="12" r="3" stroke="#64748b" stroke-width="2"/>
                    </svg>
                    <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M1 1l22 22" stroke="#64748b" stroke-width="2"/>
                      <path d="M3 3l3.59 3.59C4.22 8.29 2.62 10.03 1 12c0 0 4 7 11 7 2.11 0 4.03-.5 5.73-1.32L21 21" stroke="#64748b" stroke-width="2" stroke-linecap="round"/>
                      <path d="M9.88 9.88A3 3 0 0012 15a3 3 0 002.12-.88" stroke="#64748b" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 pt-2">
                <button
                  class="order-2 md:order-1 w-full md:w-auto rounded-full px-5 md:px-7 py-3
                         border-2 border-slate-200 text-slate-700 hover:bg-white
                         uppercase tracking-wide text-xs md:text-[12px] font-medium
                         transition"
                  @click="router.push({ name: 'ForgetPassword' })"
                  type="button"
                >
                  Lost your password
                </button>

                <button
                  class="order-1 md:order-2 w-full md:w-auto rounded-full px-6 md:px-8 py-3
                         border-2 border-emerald-600 bg-emerald-600 text-white
                         uppercase tracking-wide text-xs md:text-[12px] font-semibold
                         shadow hover:bg-white hover:text-emerald-700
                         transition disabled:opacity-40 disabled:cursor-not-allowed"
                  :disabled="isLoading || !email || !password"
                  type="submit"
                >
                  <span v-if="!isLoading">Login</span>
                  <span v-else class="inline-flex items-center gap-2">
                    <svg class="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2a10 10 0 100 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    Processing…
                  </span>
                </button>
              </div>
            </form>
          </div>

          <!-- Small helper line -->
          <p class="text-center text-slate-500 text-sm mt-4">
            Need an account? <router-link to="/signup/web" class="text-emerald-700 hover:underline">Sign up</router-link>
          </p>
        </div>
      </div>
    </div>
  </section>

  <Notification
    :show="notif.show"
    :type="notif.type"
    :message="notif.message"
    :messageDetails="notif.messageDetails"
    @closed="notifClosed"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import TopBanner from '@/components/TopBanner.vue'
import { NotificationType } from '@/types/index'
import Auth from '@/composables/Auth'
import { handleApiError, saveAuthCredentials } from '@/utils/common'
import Notification from '@/widgets/Notification.vue'
import { useRouter } from 'vue-router'

const passwordFieldtype = ref<'password' | 'text'>('password')
const email = ref<string>('')
const password = ref<string>('')
const isLoading = ref<boolean>(false)
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const { loginUser }  = Auth()
const router = useRouter()

const togglePasswordFieldType = () :void => {
  passwordFieldtype.value = passwordFieldtype.value === 'password' ? 'text' : 'password'
}

const handleLogin = async () : Promise<void> => {
  if (email.value !== '' && password.value !== '' && !isLoading.value) {
    isLoading.value = true
    const response = await loginUser(email.value, password.value)
    isLoading.value = false

    if (response.status === 200) {
      const accountType = saveAuthCredentials(response.data)
      if (accountType === 'agent' || accountType === 'corporate') {
        router.push({ name: 'AccountDashboard' })
      } else if (accountType === 'web') {
        router.push({ name: 'UserSettings', params: { userType: 'web' } })
      } else {
        router.push({ name: 'Home' })
      }
    } else {
      const error = handleApiError(response)
      notif.value = { show: true, type: 'error', message: error, messageDetails: '' }
      setTimeout(() => { notifClosed() }, 4000)
    }
  }
}

const notifClosed = () : void => {
  notif.value.show = false
}
</script>